import { PaymentService } from '@/services/payments';
import {
  ApiErrorResponse,
  ApiPaginationParams,
  ApiQueryParams,
  ApiReactQueryOptions,
  IncludeApiQueryParam,
  ISortingParams,
} from '@/types/api';
import {
  ApiIndividualPayment,
  ApiPaginatedPayments,
  ApiPayPayments,
  PaymentPayMethod,
} from '@/types/payment';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

export const getPaymentsQueryOptions = (
  filters?: ApiQueryParams,
  pagination?: ApiPaginationParams,
  include?: IncludeApiQueryParam,
  sorting?: ISortingParams,
  options?: ApiReactQueryOptions<ApiPaginatedPayments>,
) => ({
  queryKey: [PaymentService.PATH, filters, pagination, sorting],
  queryFn: async () =>
    await PaymentService.getPayments(filters, pagination, include, sorting),
  ...options,
});

export const useGetPayments = (
  filters: ApiQueryParams = {},
  pagination: ApiPaginationParams = {},
  include?: IncludeApiQueryParam,
  sorting?: ISortingParams,
  options?: ApiReactQueryOptions<ApiPaginatedPayments>,
) => {
  return useQuery<ApiPaginatedPayments, ApiErrorResponse>(
    getPaymentsQueryOptions(filters, pagination, include, sorting, options),
  );
};

export function usePayPayments() {
  const queryClient = useQueryClient();
  const mutationKey = [PaymentService.PATH, 'pay'];

  return useMutation<
    ApiPayPayments,
    ApiErrorResponse,
    {
      paymentIds: string[];
      paymentMethod: PaymentPayMethod;
    }
  >({
    mutationKey: mutationKey,
    mutationFn: async data => {
      if (!data.paymentIds?.length) {
        throw new Error('Payments are required for mutation');
      }

      return PaymentService.pay(data);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PaymentService.PATH],
        exact: false,
      });
    },
  });
}

export function useIndividualPayments(
  paymentIds: string[],
  options?: ApiReactQueryOptions<ApiIndividualPayment>,
) {
  return useQueries({
    queries: paymentIds.map(id => ({
      queryKey: [PaymentService.PATH, id],
      queryFn: () => PaymentService.getPayment(id),
      ...options,
    })),
    combine: results => {
      return {
        data: results.map(result => result.data?.data),
        isLoading: results.some(result => result.isLoading),
        isError: results.some(result => result.isError),
        error: results.find(result => result.isError)?.error,
      };
    },
  });
}
